import React, { useMemo, useState } from 'react';

function fmtDate(s) {
  if (!s) return '';
  const str = String(s);
  if (str.length === 8) return `${str.slice(0, 4)}.${str.slice(4, 6)}.${str.slice(6, 8)}`;
  if (str.length === 10 && str.includes('-')) return str.replaceAll('-', '.');
  return str;
}

function normalizeResultLabel(result) {
  const value = String(result ?? '').trim();
  if (!value) return '정보 없음';
  return value.replaceAll('질환의심', '질환 의심');
}

function resolveResultTone(label) {
  const value = String(label ?? '').trim();
  if (!value || value === '정보 없음') return 'unknown';
  if (/(의심|이상|위험|유질환)/.test(value)) return 'danger';
  if (/(경계|전단계|주의)/.test(value)) return 'borderline';
  if (/정상/.test(value)) return 'normal';
  return 'unknown';
}

function hasCheckupContent(item) {
  if (!item) return false;
  return Boolean(item.date || item.facility || String(item.result ?? '').trim());
}

function checkupItemKey(item) {
  if (!item) return '';
  return `${item.date ?? ''}|${item.facility ?? ''}|${item.result ?? ''}`;
}

function toneColor(tone) {
  if (tone === 'danger') return '#b4233e';
  if (tone === 'borderline') return '#9a6507';
  if (tone === 'normal') return '#0e7a54';
  return '#6b7280';
}

export default function NhisHeroBanner({ latestCheckup, checkupHistory }) {
  const [showAllHistory, setShowAllHistory] = useState(false);

  const history = useMemo(
    () => (Array.isArray(checkupHistory) ? checkupHistory.filter(hasCheckupContent) : []),
    [checkupHistory],
  );

  const latest = useMemo(() => {
    if (hasCheckupContent(latestCheckup)) return latestCheckup;
    return history[0] ?? null;
  }, [latestCheckup, history]);

  const previousHistory = useMemo(() => {
    if (!history.length) return [];
    if (!latest) return history;
    const latestKey = checkupItemKey(latest);
    if (checkupItemKey(history[0]) === latestKey) return history.slice(1);
    return history.filter((item) => checkupItemKey(item) !== latestKey);
  }, [history, latest]);

  const visibleHistory = showAllHistory ? previousHistory : previousHistory.slice(0, 2);

  const normalizedResult = normalizeResultLabel(latest?.result);
  const hasResult = normalizedResult !== '정보 없음';
  const hasData = Boolean(latest?.date || hasResult || latest?.facility || previousHistory.length);
  const resultTone = resolveResultTone(normalizedResult);

  const infoRowStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    fontSize: 13,
    lineHeight: 1.45,
    color: '#334155'
  };

  const infoLabelStyle = {
    minWidth: 58,
    color: '#6b7280',
    fontWeight: 600,
    flexShrink: 0,
  };

  const resultValueStyle = { color: toneColor(resultTone), fontWeight: 700 };

  return (
    <div style={{
      background: '#ffffff',
      borderBottom: '1px solid #e5edf8',
      padding: '16px 16px 14px',
    }}>
      <p style={{
        margin: 0,
        fontSize: 11,
        fontWeight: 700,
        color: '#5f7590',
        letterSpacing: '0.01em',
      }}>
        건강 데이터 대시보드
      </p>

      <h1 style={{
        margin: '6px 0 4px',
        fontSize: 18,
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
        fontWeight: 760,
        color: '#102036',
      }}>
        검진·진료·투약 정보를 한눈에 확인
      </h1>
      <p style={{
        margin: 0,
        fontSize: 13,
        lineHeight: 1.55,
        color: '#62758e',
      }}>
        일반검진 결과와 진료내역, 투약 이력을 함께 요약해 보여줍니다.
      </p>

      <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
        {hasData && (
          <div
            style={{
              border: '1px solid #e2e8f0',
              background: '#f8fafc',
              borderRadius: 10,
              padding: '10px 12px',
              display: 'grid',
              gap: 6,
            }}
          >
            {latest?.date && (
              <div style={infoRowStyle}>
                <span style={infoLabelStyle}>검진일자</span>
                <span style={{ color: '#102036', fontWeight: 600 }}>{fmtDate(latest.date)}</span>
              </div>
            )}

            {latest?.facility && (
              <div style={infoRowStyle}>
                <span style={infoLabelStyle}>검진기관</span>
                <span style={{ color: '#475569' }} title={latest.facility}>
                  {latest.facility}
                </span>
              </div>
            )}

            <div style={infoRowStyle}>
              <span style={infoLabelStyle}>검진판정</span>
              <span style={resultValueStyle}>
                {normalizedResult}
              </span>
            </div>

            {previousHistory.length > 0 && (
              <div style={{
                marginTop: 2,
                borderTop: '1px dashed #d6e0ec',
                paddingTop: 8,
                display: 'grid',
                gap: 6,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#60748e' }}>이전 검진 이력</span>
                  {previousHistory.length > 2 && (
                    <button
                      type="button"
                      onClick={() => setShowAllHistory((prev) => !prev)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: '#1763ff',
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      {showAllHistory ? '접기' : `${previousHistory.length - 2}건 더보기`}
                    </button>
                  )}
                </div>

                <div style={{ display: 'grid', gap: 6 }}>
                  {visibleHistory.map((item, idx) => {
                    const itemResult = normalizeResultLabel(item?.result);
                    const itemTone = resolveResultTone(itemResult);
                    return (
                      <div
                        key={`${checkupItemKey(item)}-${idx}`}
                        style={{
                          border: '1px solid #e5edf7',
                          borderRadius: 8,
                          background: '#ffffff',
                          padding: '7px 9px',
                          display: 'grid',
                          gap: 3,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#334155' }}>
                            {item?.date ? fmtDate(item.date) : '-'}
                          </span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: toneColor(itemTone) }}>{itemResult}</span>
                        </div>
                        <p style={{
                          margin: 0,
                          fontSize: 11,
                          color: '#5f7289',
                          lineHeight: 1.35,
                        }}>
                          {item?.facility || '기관 정보 없음'}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {!hasData && (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            width: 'fit-content',
            fontSize: 12,
            fontWeight: 700,
            color: '#6b7280',
            background: '#f3f4f6',
            border: '1px solid #e5e7eb',
            borderRadius: 999,
            padding: '5px 10px',
          }}>
            수집된 검진 데이터가 없습니다
          </span>
        )}
      </div>
    </div>
  );
}
